import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row, Form } from 'react-bootstrap';
import ModalCustom from './ModalCustom';
import CustomInput from 'components/backoffice/customInput';
import Button from 'components/backoffice/buttonCustom';
import SpinnerComponent from 'components/backoffice/SpinnerComponent';
import { createTheme } from 'api/company';

const HEX_REGEX = /^#([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$/;

const ThemeModal = ({
  show,
  onHide,
  onThemeCreated,
  loading = false
}) => {
  const { t } = useTranslation(['backoffice']);
  const [isLoading, setIsLoading] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    defaultValues: {
      name: '',
      color_primario: '#000000',
      color_secundario: '#D9DBDE',
      color_terciario: '#0069d9',
      color_sombra: '#8f8f8f',
      texto_primario: '#000000',
      texto_secundario: '#FFFFFF',
      boton_primario: '#0069d9',
      boton_secundario: '#FFFFFF',
      fuente_primaria: 'montserrat'
    }
  });

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const payload = {
        name: data.name || null,
        color_primario: data.color_primario,
        color_secundario: data.color_secundario,
        color_terciario: data.color_terciario,
        color_sombra: data.color_sombra,
        texto_primario: data.texto_primario,
        texto_secundario: data.texto_secundario,
        boton_primario: data.boton_primario,
        boton_secundario: data.boton_secundario,
        fuente_primaria: data.fuente_primaria,
      };

      const response = await createTheme(payload);

      const status = response?.status ?? 201;
      const resultData = response?.data ?? response;

      if (status === 200 || status === 201) {
        reset();
        onThemeCreated?.(resultData);
        onHide();
      }
    } catch (error) {
      console.error('Error al crear el tema:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    onHide();
  };

  const colorRules = {
    pattern: {
      value: HEX_REGEX,
      message: t('notHexColor')
    }
  };

  const footer = (
    <div className="d-flex gap-3">
      <Button
        color="secondary"
        onClick={handleClose}
        disabled={isLoading}
      >
        {t('cancel')}
      </Button>
      <Button
        color="primary"
        onClick={handleSubmit(onSubmit)}
        loading={isLoading}
      >
        {t('crear')}
        {isLoading && <SpinnerComponent loading={isLoading} />}
      </Button>
    </div>
  );

  return (
    <ModalCustom
      show={show}
      onHide={handleClose}
      title={t('themes.createTheme')}
      size="xl"
      footer={footer}
    >
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Row className="mt-3">
          <Col sm={12} className="mb-3">
            <Controller
              name="name"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido')
                }
              }}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="text"
                  label={t('themes.themeName')}
                  placeholder={t('themes.themeName')}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
            {errors?.name && <small className="text-danger">{errors.name.message}</small>}
          </Col>
        </Row>

        <Row className="mt-3">
          <Col sm={3} className="mb-3">
            <Controller
              name="color_primario"
              control={control}
              rules={colorRules}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="color"
                  label={t('themes.primaryColor')}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
            {errors?.color_primario && <small className="text-danger">{errors.color_primario.message}</small>}
          </Col>
          <Col sm={3} className="mb-3">
            <Controller
              name="color_secundario"
              control={control}
              rules={colorRules}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="color"
                  label={t('themes.secondaryColor')}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
            {errors?.color_secundario && <small className="text-danger">{errors.color_secundario.message}</small>}
          </Col>
          <Col sm={3} className="mb-3">
            <Controller
              name="color_terciario"
              control={control}
              rules={colorRules}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="color"
                  label={t('themes.tertiaryColor')}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
            {errors?.color_terciario && <small className="text-danger">{errors.color_terciario.message}</small>}
          </Col>
          <Col sm={3} className="mb-3">
            <Controller
              name="color_sombra"
              control={control}
              rules={colorRules}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="color"
                  label={t('themes.shadowColor')}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
            {errors?.color_sombra && <small className="text-danger">{errors.color_sombra.message}</small>}
          </Col>
        </Row>

        <Row className="mt-3">
          <Col sm={3} className="mb-3">
            <Controller
              name="texto_primario"
              control={control}
              rules={colorRules}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="color"
                  label={t('themes.primaryText')}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
            {errors?.texto_primario && <small className="text-danger">{errors.texto_primario.message}</small>}
          </Col>
          <Col sm={3} className="mb-3">
            <Controller
              name="texto_secundario"
              control={control}
              rules={colorRules}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="color"
                  label={t('themes.secondaryText')}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
            {errors?.texto_secundario && <small className="text-danger">{errors.texto_secundario.message}</small>}
          </Col>
          <Col sm={3} className="mb-3">
            <Controller
              name="boton_primario"
              control={control}
              rules={colorRules}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="color"
                  label={t('themes.primaryButton')}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
            {errors?.boton_primario && <small className="text-danger">{errors.boton_primario.message}</small>}
          </Col>
          <Col sm={3} className="mb-3">
            <Controller
              name="boton_secundario"
              control={control}
              rules={colorRules}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="color"
                  label={t('themes.secondaryButton')}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
            {errors?.boton_secundario && <small className="text-danger">{errors.boton_secundario.message}</small>}
          </Col>
          <Col sm={3} className="mb-3">
            <Controller
              name="fuente_primaria"
              control={control}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="text"
                  label={t('themes.primaryFont')}
                  placeholder={t('themes.primaryFont')}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
          </Col>
        </Row>
      </Form>
    </ModalCustom>
  );
};

ThemeModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onThemeCreated: PropTypes.func.isRequired,
  loading: PropTypes.bool
};

export default ThemeModal;